import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getVocabMastery } from '../api/students';
import { getStudentDisplayName } from '../../../config/studentNameMapping';
import { getStudentPerformances } from '../config/performancesConfig';
import { useAuth } from '../../../app/providers/AuthContext';
import { useCanFetch } from '../../../app/hooks/useCanFetch';
import { getChinaHour, getTodayInChina } from '../../../utils/timestampUtils';
import { getLessonLabelCache } from '../../../utils/lessonTitle';
import { getLessonsByBook } from '../../../data/lessonsRegistry';
import { reportUserFacingError } from '../../../utils/errorReporting';
import LevelUpModal from './LevelUpModal';
import './StudentDashboard.css';
import { LambLoader } from '../../../shared/components';
import { lazyWithRetry } from '../../../utils/lazyWithRetry';
import DailyAssignments from './DailyAssignments';
// Deep import on purpose — the feature barrel also pulls in DailyTestPage
// (quiz registry + course configs), which must stay out of the dashboard chunk.
import { DailyTestCard } from '../../dailyTest/components/DailyTestCard';
import BirthYmPrompt from '../../childAge/BirthYmPrompt';
import { useHomeworkGate } from '../../dailyTest/hooks/useHomeworkGate';
import { HomeworkLockedModal } from '../../dailyTest/components/HomeworkLockedModal';
const ProgressPage = lazy(() => import('../../progress/components/ProgressPage'));
import { CategoryLevelsCard } from '../../categoryLevels/components/CategoryLevelsCard';
import { ArrowLeft } from 'lucide-react';
import MobileStudentNav, { MobileStudentTopBar, ladderPointsFor } from './MobileStudentNav';
import ReviewFeedbackModal from './ReviewFeedbackModal';
import EmptyState from './EmptyState';
import SettingsContent from './SettingsContent';
import VideoMessageList from './VideoMessageCard';

// Lazy — only mounts once a kid actually opens a message, which is rare
// enough that its markup should not sit in the dashboard chunk.
const VideoMessagePlayer = lazy(() => import('./VideoMessagePlayer'));

// Lazy-loaded — QRPage (lesson viewer) preloads HLS.js; keeping it out of
// the dashboard's critical import graph shaves ~100KB+ from first paint.
// Only rendered when a teacher opens an assignment preview.
const QRPage = lazyWithRetry(() => import('../../lessonViewer').then(m => ({ default: m.QRPage })), 'QRPage_DashPreview');

// Lazy-loaded tab content components (code-split for faster initial load)
const MapContent = lazy(() => import('./MapContent'));
const PerformancesContent = lazy(() => import('./PerformancesContent'));
const CartoonsContent = lazy(() => import('./CartoonsContent'));
const LevelProgressPage = lazy(() => import('./LevelProgressPage'));
const StudentCalendar = lazy(() => import('./StudentCalendar'));
const HistoryContent = lazy(() => import('./HistoryContent'));

// Custom hooks
import { useDashboardData, useStreakData, useStudentCoins, useReviewModals, useVideoMessages } from '../hooks';

// Constants
import { TIME_OF_DAY_UPDATE_INTERVAL } from '../constants';

// Core CSS (always needed)
import './DailyAssignments.css';
import './LevelUpModal.css';

/**
 * Parse active tab from URL pathname.
 * @param {string} pathname
 * @returns {string}
 */
function parseActiveTab(pathname) {
  // if (pathname.includes('/map')) return 'map';  // hidden — vocab tab not ready
  if (pathname.includes('/history')) return 'daily';
  if (pathname.includes('/calendar')) return 'daily';

  if (pathname.includes('/performances')) return 'performances';
  if (pathname.includes('/cartoons')) return 'cartoons';
  if (pathname.includes('/levels')) return 'levels';
  if (pathname.includes('/progress')) return 'progress';
  return 'daily';
}

/**
 * Map China hour to time-of-day period for background theme.
 * @returns {'morning' | 'afternoon' | 'evening' | 'night'}
 */
function getTimeOfDay() {
  const hour = getChinaHour();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 20) return 'evening';
  return 'night';
}

/**
 * Count items in a lesson config (vocab, sentences, letters, etc.)
 */
function countLessonItems(lesson) {
  let count = 0;
  if (lesson?.pages) {
    Object.values(lesson.pages).forEach(page => {
      if (page?.sections) {
        Object.values(page.sections).forEach(sectionItems => {
          if (Array.isArray(sectionItems)) {
            count += sectionItems.length;
          }
        });
      }
    });
  }
  return count;
}

/**
 * Unified Student Dashboard - Single parent component for all tabs.
 * Fetches shared data once, renders appropriate tab content.
 */
function StudentDashboard({ studentId: propStudentId, isTeacherView = false }) {
  const { studentId: urlStudentId } = useParams();
  const studentId = propStudentId || urlStudentId;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSignedIn, signOut, familyMembers, effectiveStudentId, switchStudent } = useAuth();
  const canFetch = useCanFetch();

  // For teacher view, use local state for tabs (URL doesn't change)
  const [teacherActiveTab, setTeacherActiveTab] = useState('daily');

  // Determine active tab from URL (student view) or local state (teacher view)
  const activeTab = isTeacherView ? teacherActiveTab : parseActiveTab(location.pathname);

  // ===== Custom Hooks =====

  // Dashboard data with visibility-based refetch
  const { dashboard, loading, error } = useDashboardData(canFetch ? studentId : null);
  // The child's 出生年月: what the dashboard loaded, or what was just saved from
  // settings or the one-time card (the dashboard hook has no refetch).
  // Keyed to the child it was saved for: switching family accounts reuses this
  // mounted page, and one child's date must never show on another's.
  const [savedBirth, setSavedBirth] = useState(null);
  const setSavedBirthYm = useCallback((ym) => setSavedBirth({ studentId, ym }), [studentId]);
  const birthYm = savedBirth?.studentId === studentId
    ? savedBirth.ym
    : (dashboard ? (dashboard.birthYm ?? null) : undefined);

  // Streak/coin data from API
  const { streakData, setStreakData, refetch: refetchStreak } = useStreakData(studentId, isSignedIn, user);

  // Coin calculations + level-up detection
  const {
    totalEarnedCoins,
    availableCoins,
    levelUpData,
    handleQuizXpEarned,
    clearLevelUp,
  } = useStudentCoins(streakData);

  // Review modals (unseen + history)
  const {
    showReviewModal,
    unseenReviews,
    showHistoryModal,
    reviewHistory,
    handleCloseReviewModal,
    handleNotificationClick,
    handleCloseHistoryModal,
    unseenCount,
  } = useReviewModals(studentId, isSignedIn, user, isTeacherView, refetchStreak);

  // Teacher video messages — pinned above the to-do list until watched.
  const {
    messages: videoMessages,
    activeMessage: activeVideoMessage,
    openMessage: openVideoMessage,
    closeMessage: closeVideoMessage,
    markOpened: markVideoMessageOpened,
    markFinished: markVideoMessageFinished,
    markDone: markVideoMessageDone,
    markingDone: markingVideoMessageDone,
  } = useVideoMessages(studentId, isSignedIn, user);

  // Homework gate — when a teacher has walled this student behind the daily
  // test, the to-do cards grey out and explain themselves on tap rather than
  // opening. A teacher looking at the dashboard sees it exactly as the kid does,
  // so `skip` is not set here: this view never navigates anywhere.
  const homeworkGate = useHomeworkGate(isTeacherView ? null : studentId);
  const [showLockedModal, setShowLockedModal] = useState(false);

  // ===== Local State =====

  // Lesson label cache for formatting
  const [lessonLabelCache, setLessonLabelCache] = useState(null);

  // Book lessons and item counts for map tab
  const [bookLessons, setBookLessons] = useState({});
  const [itemCountsCache, setItemCountsCache] = useState({});

  // Vocab mastery data (for map tab) — loaded lazily when the map tab opens
  const [vocabMastery, setVocabMastery] = useState({});
  const [vocabMasteryLoading, setVocabMasteryLoading] = useState(false);

  // For teacher preview of lesson content
  const [previewAssignment, setPreviewAssignment] = useState(null);

  // Calendar overlay state
  const [showCalendar, setShowCalendar] = useState(false);

  // Settings bottom sheet state
  const [showSettings, setShowSettings] = useState(false);

  // History bottom sheet state
  const [showHistory, setShowHistory] = useState(false);

  // Time-of-day background theme (updates every minute)
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay);

  // Refs for header coin display and nav map badge
  const coinTargetRef = useRef(null);
  const mapTargetRef = useRef(null);

  // ===== Derived State =====

  const displayName = getStudentDisplayName(studentId, user?.name);
  const performances = getStudentPerformances(studentId);

  // Badge count — prefer the server-provided total (accurate when todos are
  // capped at 100 for heavy users); fall back to array length for safety.
  const dailyAssignmentCount = useMemo(() => {
    if (!dashboard) return 0;
    return dashboard.todoAssignmentsCount ?? dashboard.todoAssignments?.length ?? 0;
  }, [dashboard]);


  // ===== Effects =====

  // Time-of-day update
  useEffect(() => {
    const interval = setInterval(() => setTimeOfDay(getTimeOfDay()), TIME_OF_DAY_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Load lesson label cache on mount
  useEffect(() => {
    getLessonLabelCache()
      .then(cache => setLessonLabelCache(cache))
      .catch(err => console.error('Error loading lesson label cache:', err));
  }, []);

  // Load vocab mastery only when the map tab opens. Map tab is currently
  // hidden (parseActiveTab line 47) so this is a no-op today; kept tab-gated
  // so unhiding the tab won't re-introduce the mount-time fetch. Ref tracks
  // the studentId last loaded so tab toggles don't refetch but student
  // switches do — a separate reset effect with `user` in deps would
  // infinite-loop when the auth context returns a fresh user reference per render.
  const vocabMasteryLoadedForRef = useRef(null);
  useEffect(() => {
    if (activeTab !== 'map') return;
    if (!studentId || !isSignedIn || !user) return;
    if (vocabMasteryLoadedForRef.current === studentId) return;
    vocabMasteryLoadedForRef.current = studentId;

    let cancelled = false;
    setVocabMasteryLoading(true);
    getVocabMastery(studentId)
      .then(data => {
        if (cancelled) return;
        setVocabMastery(data?.vocabMastery || {});
        setVocabMasteryLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        // Intentionally leave loadedForRef set to studentId so we don't retry —
        // an unstable user reference from auth context re-renders would otherwise
        // turn a permanent error (bad token, hard server failure) into a tight
        // retry loop. Page refresh is the recovery path.
        console.error('Error loading vocab mastery:', err);
        setVocabMasteryLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, studentId, isSignedIn, user]);

  // Load book lessons for map (when dashboard is ready)
  useEffect(() => {
    if (!dashboard) return;

    const loadBookLessons = async () => {
      const allAssignments = [
        ...(dashboard.todoAssignments || []),
        ...(dashboard.upcomingAssignments || []),
        ...(dashboard.pastAssignments || []),
      ];

      const enrolledBooks = new Set();
      allAssignments.forEach(a => {
        if (a.topic && a.batch) {
          enrolledBooks.add(`${a.topic}_${a.batch}`);
        }
      });

      const lessonsMap = {};
      const countsMap = {};

      for (const bookKey of enrolledBooks) {
        const [topic, batch] = bookKey.split('_');
        try {
          const lessons = await getLessonsByBook(topic, batch);
          lessonsMap[bookKey] = lessons;

          // Build item counts for each lesson
          lessons.forEach(lesson => {
            const lessonNum = parseInt(lesson.lessonNumber, 10);
            const key = `${topic}_${batch}_${lessonNum}`;
            countsMap[key] = countLessonItems(lesson);
          });
        } catch (err) {
          reportUserFacingError(err, { context: 'StudentDashboard: load lessons for enrolled book' });
          lessonsMap[bookKey] = [];
        }
      }

      setBookLessons(lessonsMap);
      setItemCountsCache(countsMap);
    };

    loadBookLessons();
  }, [dashboard]);

  // ===== Handlers =====

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      reportUserFacingError(error, { context: 'StudentDashboard: sign out' });
    }
  };

  const handleSwitchAccount = (newStudentId) => {
    switchStudent(newStudentId);
    navigate(`/dashboard/${newStudentId}`);
  };

  const handleSettingsClick = () => {
    setShowSettings(prev => !prev);
  };

  // ===== Render Helpers =====

  // Teacher preview: show QRPage inline when an assignment is selected
  if (isTeacherView && previewAssignment) {
    return (
      <div className="lesson-preview-container">
        <div className="lesson-preview-header">
          <button
            className="lesson-preview-back-btn"
            onClick={() => setPreviewAssignment(null)}
          >
            <ArrowLeft size={18} />
            <span>返回学生视图</span>
          </button>
        </div>
        <Suspense fallback={<LambLoader />}>
          <QRPage
            inlineProps={{
              courseName: previewAssignment.topic,
              bookNumber: previewAssignment.batch,
              lessonNumber: String(previewAssignment.lesson_number).padStart(3, '0'),
              projectId: previewAssignment.project_id,
              reviewIndex: previewAssignment.reviewIndex || 0,
              waveId: previewAssignment.wave_id || 0,
              includedItems: previewAssignment.included_items
                ? JSON.parse(previewAssignment.included_items)
                : null,
              studentId: studentId,
              assignmentId: previewAssignment.id,
            }}
          />
        </Suspense>
      </div>
    );
  }

  // Tab content — gated on loading/error so the shell renders instantly
  const renderTabContent = () => {
    if (loading) {
      return <LambLoader />;
    }

    if (error || !dashboard) {
      return (
        <div className="error-container">
          <p>{error || 'Failed to load dashboard'}</p>
        </div>
      );
    }

    const tabContent = {
      daily: () => {
        const hasTodo = dashboard?.todoAssignments?.length > 0;
        const [, month, day] = getTodayInChina().split('-');
        return (
          <>
            {/* Asked of a parent, so only for students in a class: adult self-study
                accounts are not someone's child (they can still set it in ⚙️). */}
            {!isTeacherView && dashboard?.enrolledClasses?.length > 0 && (
              <BirthYmPrompt key={studentId} studentId={studentId} birthYm={birthYm} onSaved={setSavedBirthYm} />
            )}
            <h2 className="daily-list-title">今日作业 · {Number(month)}月{Number(day)}日</h2>
            <DailyTestCard
              studentId={studentId}
              // The roster name of the child being VIEWED, so the dragon greets
              // her and not the parent whose phone it is. Account name as fallback.
              studentName={dashboard?.studentName || user?.name}
              isTeacherView={isTeacherView}
              // Same student-id guard as the tutor-tab dragon (QRPage): a flag
              // read under one account must never greet a sibling by the wrong name.
              dragon={!!user?.features?.quizDragon && user?.features_student_id === studentId}
              points={ladderPointsFor(studentId, availableCoins)}
            />
            {!isTeacherView && <CategoryLevelsCard studentId={studentId} />}
            {/* Above the assignments on purpose — the whole point is that the
                kid sees it before starting homework. Teachers see the card but
                cannot open it; pressing done would consume the kid's message. */}
            <VideoMessageList
              messages={videoMessages}
              onOpen={openVideoMessage}
              disabled={isTeacherView}
            />
            {hasTodo ? (
              <DailyAssignments
                assignments={dashboard.todoAssignments}
                studentId={studentId}
                lessonLabelCache={lessonLabelCache}
                itemCountsCache={itemCountsCache}
                isTeacherView={isTeacherView}
                locked={homeworkGate.gated}
                onLockedClick={() => setShowLockedModal(true)}
                onTeacherClick={setPreviewAssignment}
                bookLessons={bookLessons}
              />
            ) : (
              <EmptyState mascot="✅" mascotStyle="reading" title="还没有作业" size="large" />
            )}
            {showCalendar && (
              <div className="calendar-overlay" onClick={() => setShowCalendar(false)}>
                <div className="calendar-overlay__content" onClick={e => e.stopPropagation()}>
                  <button className="calendar-overlay__close" onClick={() => setShowCalendar(false)}>✕</button>
                  <Suspense fallback={<LambLoader />}>
                    <StudentCalendar studentId={studentId} phonicsLetterLookup={lessonLabelCache} />
                  </Suspense>
                </div>
              </div>
            )}
          </>
        );
      },
      map: () => (
        <MapContent
          dashboard={dashboard}
          bookLessons={bookLessons}
          studentId={studentId}
          vocabMastery={vocabMastery}
          vocabMasteryLoading={vocabMasteryLoading}
          onQuizXpEarned={handleQuizXpEarned}
        />
      ),
      performances: () => <PerformancesContent performances={performances} />,
      cartoons: () => (
        <CartoonsContent
          studentId={studentId}
          navigate={navigate}
        />
      ),
      levels: () => (
        <LevelProgressPage
          points={ladderPointsFor(studentId, availableCoins)}
          studentId={studentId}
          onBack={() => navigate(`/dashboard/${studentId}`)}
        />
      ),
      // Parents read this on the child's phone — the family shares one device, so
      // there is no separate parent login to put it behind.
      progress: () => (
        <ProgressPage studentId={studentId} displayName={displayName} />
      ),
    };

    return (tabContent[activeTab] || (() => null))();
  };

  // Tab loading fallback for lazy-loaded components
  const TabLoader = () => (
    <div className="tab-loading">
      <LambLoader />
    </div>
  );

  return (
    <div className={`student-dashboard time-${timeOfDay}`}>
      {/* Header with top bar */}
      <div className="sticky-header-wrapper">
        <MobileStudentTopBar
          studentId={studentId}
          displayName={displayName}
          totalCoins={streakData?.points !== undefined ? availableCoins : undefined}
          coinRef={coinTargetRef}
          isTeacherView={isTeacherView}
          onSettingsClick={handleSettingsClick}
          onCalendarClick={() => setShowCalendar(true)}
          onNotificationClick={handleNotificationClick}
          onHistoryClick={() => setShowHistory(true)}
          unseenCount={unseenCount}
        />
      </div>

      <div className="dashboard-content">
        <Suspense fallback={<TabLoader />}>
          {renderTabContent()}
        </Suspense>
      </div>

      {/* Bottom nav removed — settings moved to top bar */}
      {false && <MobileStudentNav
        studentId={studentId}
        mapRef={mapTargetRef}
        dailyCount={dailyAssignmentCount}
        isTeacherView={isTeacherView}
        activeTab={activeTab}
        onTabChange={isTeacherView ? setTeacherActiveTab : undefined}
      />}

      {/* Review feedback modal (auto-popup for unseen reviews) */}
      <ReviewFeedbackModal
        isOpen={showReviewModal}
        mode="unseen"
        reviews={unseenReviews.reviews}
        summary={unseenReviews.summary}
        assignments={unseenReviews.assignments}
        quizzes={unseenReviews.quizzes}
        points={ladderPointsFor(studentId, availableCoins)}
        lessonLabelCache={lessonLabelCache}
        onClose={handleCloseReviewModal}
      />

      {/* Review history modal (manual open via bell icon) */}
      <ReviewFeedbackModal
        isOpen={showHistoryModal}
        mode="history"
        reviews={reviewHistory.reviews}
        summary={reviewHistory.summary}
        assignments={reviewHistory.assignments}
        points={ladderPointsFor(studentId, availableCoins)}
        lessonLabelCache={lessonLabelCache}
        onClose={handleCloseHistoryModal}
      />

      {/* Why a tapped homework card did not open */}
      {showLockedModal && (
        <HomeworkLockedModal
          owed={homeworkGate.owed}
          studentId={studentId}
          onClose={() => setShowLockedModal(false)}
        />
      )}

      {/* Teacher video message player */}
      {activeVideoMessage && (
        <Suspense fallback={<LambLoader />}>
          <VideoMessagePlayer
            message={activeVideoMessage}
            onDone={markVideoMessageDone}
            onKeep={closeVideoMessage}
            onOpened={markVideoMessageOpened}
            onFinished={markVideoMessageFinished}
            onClose={closeVideoMessage}
            busy={markingVideoMessageDone}
          />
        </Suspense>
      )}

      {/* Level-up celebration modal */}
      {levelUpData && (
        <LevelUpModal
          studentId={studentId}
          previousLevel={levelUpData.previousLevel}
          newLevel={levelUpData.newLevel}
          onClose={clearLevelUp}
        />
      )}

      {/* History bottom sheet */}
      {showHistory && createPortal(
        <div className="level-sheet-overlay" onClick={() => setShowHistory(false)} data-testid="history-sheet-overlay">
          <div className="history-sheet level-sheet" onClick={e => e.stopPropagation()}>
            <div className="level-sheet-handle" />
            <Suspense fallback={<LambLoader />}>
              <HistoryContent
                dashboard={dashboard}
                studentId={studentId}
                lessonLabelCache={lessonLabelCache}
                itemCountsCache={itemCountsCache}
                isTeacherView={isTeacherView}
                onTeacherClick={setPreviewAssignment}
              />
            </Suspense>
          </div>
        </div>,
        document.body
      )}

      {/* Settings bottom sheet */}
      {showSettings && createPortal(
        <div className="level-sheet-overlay" onClick={() => setShowSettings(false)} data-testid="settings-sheet-overlay">
          <div className="settings-sheet level-sheet" onClick={e => e.stopPropagation()}>
            <div className="level-sheet-handle" />
            <SettingsContent
              onSignOut={handleSignOut}
              familyMembers={familyMembers}
              currentStudentId={effectiveStudentId}
              onSwitchAccount={handleSwitchAccount}
              studentId={isTeacherView ? undefined : studentId}
              birthYm={birthYm}
              onBirthYmSaved={setSavedBirthYm}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export { StudentDashboard };
export default StudentDashboard;
